/*
 * Oath — FAQ sticky tab nav (FAQ PAGE only).
 * Builds the sticky tab bar from the native FAQ category sections on the page
 * (native section + accordion composition), wires click -> anchor-scroll (offset
 * for the sticky bar), and tracks the active tab via IntersectionObserver.
 * Vanilla, no deps. Pairs with sections/oath-faq-tab-nav.liquid + the native
 * page.faq.json category sections. Decision Ladder Rung 5 (genuine native gap:
 * Horizon has no sticky anchor-scroll tab-nav). L-OATH-S65-FAQ-PAGE.
 */
(function () {
  'use strict';

  function init() {
    var nav = document.querySelector('[data-faq-tab-nav]');
    var list = nav && nav.querySelector('[data-faq-tab-list]');
    if (!nav || !list) return;

    // FAQ category sections = native .shopify-section that contain an .accordion.
    // (hero/header/footer have none.) Filter in JS for :has-free browser support.
    var sections = Array.prototype.slice
      .call(document.querySelectorAll('.shopify-section'))
      .filter(function (s) { return s.querySelector('.accordion'); });

    if (!sections.length) {
      nav.hidden = true; // nothing to navigate
      return;
    }

    var tabsById = {};
    sections.forEach(function (sec) {
      if (!sec.id) return;
      var headingEl = sec.querySelector('h1, h2, h3, h4, h5, h6');
      var title = headingEl ? headingEl.textContent.trim() : '';
      if (!title) return;
      var li = document.createElement('li');
      li.className = 'oath-faq-tab-nav__item';
      var a = document.createElement('a');
      a.className = 'oath-faq-tab-nav__tab';
      a.href = '#' + sec.id;
      a.textContent = title;
      a.setAttribute('data-faq-tab-for', sec.id);
      li.appendChild(a);
      list.appendChild(li);
      tabsById[sec.id] = a;

      a.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToSection(sec);
        setActive(sec.id);
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '#' + sec.id);
        }
      });
    });

    function navOffset() {
      return Math.ceil(nav.getBoundingClientRect().height) || 0;
    }

    function scrollToSection(sec) {
      var top = sec.getBoundingClientRect().top + window.pageYOffset - navOffset();
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    function setActive(id) {
      Object.keys(tabsById).forEach(function (key) {
        var on = key === id;
        tabsById[key].classList.toggle('is-active', on);
        if (on) {
          tabsById[key].setAttribute('aria-current', 'true');
        } else {
          tabsById[key].removeAttribute('aria-current');
        }
      });
    }

    // A category is "active" when its top crosses just below the sticky bar.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && tabsById[entry.target.id]) {
              setActive(entry.target.id);
            }
          });
        },
        { rootMargin: '-' + (navOffset() + 8) + 'px 0px -55% 0px', threshold: 0 }
      );
      sections.forEach(function (sec) { io.observe(sec); });
    }

    // --- JS-driven sticky ---------------------------------------------------
    // CSS position:sticky CANNOT span Shopify sibling sections (each is its own
    // shopify-section wrapper, so sticky is scoped to the nav's own ~86px box).
    // Toggle position:fixed on scroll instead, with a placeholder to avoid jump,
    // releasing once the last category band has passed (so it never floats over
    // the footer). Ref: L-OATH-S72-FAQ-TABNAV-STICKY-DEFECT-001.
    var placeholder = document.createElement('div');
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.className = 'oath-faq-tab-nav__placeholder';
    placeholder.style.display = 'none';
    nav.parentNode.insertBefore(placeholder, nav);

    var stuck = false;
    var ticking = false;
    var lastSection = sections[sections.length - 1];

    function slotTop() {
      var ref = stuck ? placeholder : nav;
      return ref.getBoundingClientRect().top + window.pageYOffset;
    }

    function applyStuck() {
      var rect = nav.getBoundingClientRect();
      placeholder.style.height = rect.height + 'px';
      placeholder.style.display = 'block';
      nav.style.position = 'fixed';
      nav.style.top = '0';
      nav.style.left = rect.left + 'px';
      nav.style.width = rect.width + 'px';
      nav.classList.add('is-stuck');
      stuck = true;
    }

    function releaseStuck() {
      nav.style.position = '';
      nav.style.top = '';
      nav.style.left = '';
      nav.style.width = '';
      nav.classList.remove('is-stuck');
      placeholder.style.display = 'none';
      stuck = false;
    }

    function updateSticky() {
      ticking = false;
      var navH = nav.offsetHeight;
      var triggerY = slotTop();
      var endY = lastSection.getBoundingClientRect().bottom + window.pageYOffset - navH;
      var y = window.pageYOffset;
      var shouldStick = y >= triggerY && y < endY;

      if (shouldStick && !stuck) {
        applyStuck();
      } else if (!shouldStick && stuck) {
        releaseStuck();
      } else if (stuck) {
        // keep aligned to the placeholder slot (e.g. after a reflow)
        var pr = placeholder.getBoundingClientRect();
        nav.style.left = pr.left + 'px';
        nav.style.width = pr.width + 'px';
      }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateSticky);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      if (stuck) releaseStuck();
      updateSticky();
    });
    updateSticky();

    var initial = (window.location.hash || '').replace('#', '');
    setActive(tabsById[initial] ? initial : sections[0].id);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
